import React, { useState } from 'react';
import { Input } from 'src/shared/components/Input';
import { Button } from 'src/shared/components/Button';
import { useForgotPasswordMutation } from 'src/services/rtk_api/auth/authApi';
import { addToast } from '@heroui/react';
import { isValidEmail } from 'src/shared/utils/validators';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { verify_recaptcha_token } from "src/services/api/verifiers/verifiers_requests";

const FORGOT_TIMEOUT_MS = 5000;

const ResetPasswordRequestForm = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const [forgotPassword, { isLoading: isSending }] = useForgotPasswordMutation();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Enter a valid email address');
      return;
    }

    if (!executeRecaptcha) {
        console.log('Execute recaptcha not yet available');
        return;
    }

    const recaptcha_token = await executeRecaptcha('submitForm');

    verify_recaptcha_token(recaptcha_token).then(async (response) => {
        const { success } = response
        if(success) {
            try {
                await forgotPassword(email).unwrap();
                setEmailSent(true);
                setCooldown(true);
                addToast({
                  title: 'Reset instructions sent to your email.',
                  severity: "success",
                  color: "success"
                });

                setTimeout(() => {
                    setCooldown(false);
                    setEmailSent(false);
                    setEmail('');
                    setError('');
                }, FORGOT_TIMEOUT_MS);
            } catch (err) {
                console.error(err);
                addToast({
                  title: 'Failed to send reset email.',
                  severity: "danger",
                  color: "danger" 
                });
            }
        } 
    }).catch((err) => {
        console.log(err)
    })
  };

  return (
    <div className="max-w-[500px] w-full mx-auto mt-10 px-4">
        <h1 className="text-2xl font-bold mb-4 text-center">Forgot your password?</h1>
        <p className="text-center mb-6">Enter your email address below and we'll send you instructions to reset your password.</p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Input
                type="email"
                label="Email"
                labelPlacement="outside"
                placeholder=" "
                value={email}
                name="email"
                onValueChange={(val) => {
                    setEmail(val);
                    if(error) setError('');
                }}
                variant="bordered" 
                isInvalid={!!error}
                errorMessage={error}/>

            <Button
                type="submit"
                className="bg-[#007CA1] text-white w-full"
                disabled={isSending || cooldown}
                >
                {cooldown ? 'Please wait...' : isSending ? 'Sending...' : 'Send Reset Email'}
            </Button>
        </form>

        {emailSent &&
            <p className="text-green-600 text-sm mt-2 text-center">
                We sent you an email with reset instructions.
            </p>
        }
    </div>
  );
};

const WrappedResetPasswordRequestForm = () => (
  <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}>
    <ResetPasswordRequestForm />
  </GoogleReCaptchaProvider>
);

export default WrappedResetPasswordRequestForm;